import { defaultLocale, locales, type Locale } from './config';

/**
 * Builds a locale-correct href for internal links.
 *
 *   localizedHref('en', '/jpg-to-png')  -> '/jpg-to-png'
 *   localizedHref('ar', '/jpg-to-png')  -> '/ar/jpg-to-png'
 *   localizedHref('ar', '/')            -> '/ar'
 *
 * `path` must be root-relative and start with '/'. External URLs (mailto:,
 * https://...) are passed through untouched since they're never localized.
 */
export function localizedHref(locale: Locale, path: string): string {
  if (/^([a-z]+:)?\/\//i.test(path) || path.startsWith('mailto:') || path.startsWith('tel:')) {
    return path;
  }
  if (locale === defaultLocale) return path;

  if (path === '/') return `/${locale}`;
  return `/${locale}${path}`;
}

/**
 * Builds the `alternates.languages` object for Next.js metadata — one entry
 * per supported locale plus 'x-default', all pointing at the same content
 * under its locale-prefixed URL.
 *
 *   hreflangAlternates('/jpg-to-png')
 *     -> { en: `${SITE_URL}/jpg-to-png`, ar: `${SITE_URL}/ar/jpg-to-png`, 'x-default': `${SITE_URL}/jpg-to-png` }
 *
 * This is intentionally NOT conditional on the CURRENT page's locale — hreflang
 * must always list every reachable language variant regardless of which
 * locale the current page is in. It IS conditional on `enabledLocales` when
 * passed: a locale an admin has toggled off in Settings (see
 * lib/i18n/config.ts's getEnabledLocales) isn't a real page to link to —
 * middleware redirects it straight back to English — so it's left out of
 * hreflang entirely rather than pointing crawlers at a redirect.
 * Defaults to every structural locale (`locales`) so existing call sites
 * that don't pass one keep their current behavior.
 */
export function hreflangAlternates(
  siteUrl: string,
  path: string,
  enabledLocales: readonly Locale[] = locales,
): Record<string, string> {
  const entries = enabledLocales.map((locale) => [locale, `${siteUrl}${localizedHref(locale, path)}`] as const);
  return {
    ...Object.fromEntries(entries),
    'x-default': `${siteUrl}${localizedHref(defaultLocale, path)}`,
  };
}

/**
 * Revalidates a locale-neutral, root-relative path (e.g. '/' or
 * '/jpg-to-png') across EVERY locale, not just the default one.
 *
 * Every one of these pages is statically generated once per locale (see
 * generateStaticParams in app/[locale]/(public)/[slug]/page.tsx — it does a
 * full locale x slug cartesian product), so `revalidatePath('/jpg-to-png')`
 * alone only busts the English HTML; the pre-rendered `/ar/jpg-to-png` copy
 * keeps serving stale content for up to the page's `revalidate` window
 * (1h) instead of updating immediately like the English page does. This
 * wraps that pattern once so every call site (tool edits, status toggles,
 * new reviews) revalidates all locales instead of silently favoring
 * English.
 */
export async function revalidateLocalizedPath(path: string): Promise<void> {
  const { revalidatePath } = await import('next/cache');
  for (const locale of locales) {
    revalidatePath(localizedHref(locale, path));
  }
}

/**
 * Strips a leading locale segment from a pathname, returning the
 * locale-neutral path. Used by the language switcher to figure out what the
 * equivalent URL is in the other language.
 *
 *   stripLocale('/ar/jpg-to-png') -> '/jpg-to-png'
 *   stripLocale('/jpg-to-png')    -> '/jpg-to-png'
 */
export function stripLocale(pathname: string, locales: readonly string[]): string {
  const segments = pathname.split('/');
  if (segments.length > 1 && locales.includes(segments[1])) {
    const rest = '/' + segments.slice(2).join('/');
    return rest === '/' ? '/' : rest.replace(/\/$/, '') || '/';
  }
  return pathname;
}
